import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  Chip,
} from "@mui/material";
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import SellRoundedIcon from "@mui/icons-material/SellRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";

import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

const emptyForm = {
  title: "",
  description: "",
  promoType: "percentage_off",
  scopeType: "global",
  productSKUs: "",
  discountPercent: "",
  discountAmount: "",
  buyQuantity: 1,
  getQuantity: 1,
  isActive: true,
  priority: 0,
  startDate: "",
  endDate: "",
  bannerText: "",
  internalNotes: "",
};

export default function AdminPromotionsPage() {
  const { style } = useStyle();

  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState(emptyForm);
  const [editingPromotionId, setEditingPromotionId] = useState(null);

  const token = useMemo(() => localStorage.getItem("token") || "", []);

  const panelBg = style.darkPanel || "rgba(9, 12, 18, 0.45)";

  const sharedTextFieldSx = {
    width: "100%",
    minWidth: 0,
    "& .MuiInputBase-root": {
      color: style.color4,
      backgroundColor: style.color2,
      borderRadius: "8px",
      minWidth: 0,
      boxSizing: "border-box",
    },
    "& .MuiOutlinedInput-root": {
      minWidth: 0,
      "& fieldset": {
        borderColor: style.color2,
      },
      "&:hover fieldset": {
        borderColor: style.color3,
      },
      "&.Mui-focused fieldset": {
        borderColor: style.accentGreen,
      },
    },
    "& .MuiInputLabel-root": {
      color: style.color3,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: style.color4,
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
      color: style.color3,
    },
    "input:-webkit-autofill": {
      WebkitBoxShadow: `0 0 0px 1000px ${style.color2} inset`,
      WebkitTextFillColor: style.color4,
      caretColor: style.color4,
      borderRadius: "8px",
    },
  };

  const sectionCardSx = {
    p: { xs: 1.5, sm: 2 },
    borderRadius: "14px",
    backgroundColor: panelBg,
    border: "1px solid rgba(255,255,255,0.08)",
    boxSizing: "border-box",
    width: "100%",
    minWidth: 0,
    overflow: "hidden",
  };

  const loadPromotions = async () => {
    try {
      setLoading(true);

      const { data } = await axios.post("/api/get-promotions", {
        token,
        includeAll: true,
      });

      if (data.error) {
        toast.error(data.error);
        return;
      }

      setPromotions(data.promotions || []);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Unable to load promotions."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    loadPromotions();
  }, []);

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingPromotionId(null);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = {
        ...prev,
        [field]: value,
      };

      if (field === "promoType") {
        if (value === "bogo") {
          if (next.discountPercent === "" || next.discountPercent === 0) {
            next.discountPercent = 100;
          }
          next.discountAmount = "";
        }

        if (value === "percentage_off") {
          next.discountAmount = "";
        }

        if (value === "fixed_amount_off") {
          next.discountPercent = "";
        }
      }

      return next;
    });
  };

  const startEdit = (promotion) => {
    setEditingPromotionId(promotion._id);
    setFormData({
      title: promotion.title || "",
      description: promotion.description || "",
      promoType: promotion.promoType || "percentage_off",
      scopeType: promotion.scopeType || "global",
      productSKUs: Array.isArray(promotion.productSKUs)
        ? promotion.productSKUs.join(", ")
        : "",
      discountPercent:
        promotion.discountPercent === 0 ? "" : promotion.discountPercent,
      discountAmount:
        promotion.discountAmount === 0 ? "" : promotion.discountAmount,
      buyQuantity: promotion.buyQuantity || 1,
      getQuantity: promotion.getQuantity || 1,
      isActive: Boolean(promotion.isActive),
      priority: promotion.priority ?? 0,
      startDate: promotion.startDate
        ? new Date(promotion.startDate).toISOString().slice(0, 16)
        : "",
      endDate: promotion.endDate
        ? new Date(promotion.endDate).toISOString().slice(0, 16)
        : "",
      bannerText: promotion.bannerText || "",
      internalNotes: promotion.internalNotes || "",
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const promotionData = {
        ...formData,
        productSKUs: formData.productSKUs,
        discountPercent:
          formData.promoType === "bogo" && (formData.discountPercent === "" || formData.discountPercent === null)
            ? 100
            : formData.discountPercent,
      };

      if (editingPromotionId) {
        const { data } = await axios.post("/api/update-promotion", {
          token,
          promotionId: editingPromotionId,
          promotionData,
        });

        if (data.error) {
          toast.error(data.error);
          return;
        }

        toast.success("Promotion updated.");
      } else {
        const { data } = await axios.post("/api/create-promotion", {
          token,
          promotionData,
        });

        if (data.error) {
          toast.error(data.error);
          return;
        }

        toast.success("Promotion created.");
      }

      resetForm();
      loadPromotions();
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Unable to save promotion."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (promotionId) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this promotion?"
    );

    if (!confirmed) return;

    try {
      const { data } = await axios.post("/api/delete-promotion", {
        token,
        promotionId,
      });

      if (data.error) {
        toast.error(data.error);
        return;
      }

      toast.success("Promotion deleted.");
      if (editingPromotionId === promotionId) {
        resetForm();
      }
      loadPromotions();
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Unable to delete promotion."
      );
    }
  };

  const formatDateTime = (value) => {
    if (!value) return "Not set";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Invalid date";
    return date.toLocaleString();
  };

  const getPromotionSummary = (promotion) => {
    if (promotion.promoType === "percentage_off") {
      return `${Number(promotion.discountPercent || 0)}% off`;
    }

    if (promotion.promoType === "fixed_amount_off") {
      return `$${Number(promotion.discountAmount || 0).toFixed(2)} off`;
    }

    if (promotion.promoType === "bogo") {
      const buyQty = Number(promotion.buyQuantity || 1);
      const getQty = Number(promotion.getQuantity || 1);
      const percent = Number(
        promotion.discountPercent === undefined ||
          promotion.discountPercent === null ||
          promotion.discountPercent === ""
          ? 100
          : promotion.discountPercent
      );

      if (percent >= 100) {
        return `Buy ${buyQty}, get ${getQty} free`;
      }

      return `Buy ${buyQty}, get ${getQty} ${percent}% off`;
    }

    return promotion.promoType;
  };

  return (
    <>
      <Seo
        title="Admin Promotions | Parish Audio"
        description="Manage promotions for Parish Audio."
        canonical="https://parishaudio.com/admin/promotions"
        robots="noindex,nofollow"
      />

      <Box
        sx={{
          backgroundColor: "black",
          minHeight: "100vh",
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          color: style.color4,
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 2, md: 3 },
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            maxWidth: 1200,
            width: "100%",
            minWidth: 0,
            mx: "auto",
            boxSizing: "border-box",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: "20px",
              overflow: "hidden",
              backgroundColor: style.color1,
              border: `1px solid ${style.color2}`,
              width: "100%",
              minWidth: 0,
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                px: { xs: 1.5, sm: 2, md: 3 },
                py: { xs: 2.25, md: 3 },
                backgroundColor: panelBg,
                borderBottom: `1px solid ${style.color2}`,
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
              >
                <Stack spacing={1} alignItems="flex-start">
                  <Chip
                    icon={<LocalOfferRoundedIcon />}
                    label="Admin"
                    sx={{
                      borderRadius: "5px",
                      fontWeight: 700,
                      color: style.color4,
                      backgroundColor: "rgba(255,255,255,0.06)",
                    }}
                  />

                  <Typography
                    sx={{
                      color: style.color4,
                      fontSize: { xs: "1.9rem", md: "2.5rem" },
                      fontWeight: 800,
                      lineHeight: 1.05,
                      letterSpacing: "-0.03em",
                    }}
                  >
                    Promotions
                  </Typography>

                  <Typography
                    sx={{
                      color: style.color3,
                      maxWidth: 760,
                      lineHeight: 1.65,
                    }}
                  >
                    Create, edit, and manage sitewide or product-specific
                    promotions for your store.
                  </Typography>
                </Stack>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  sx={{ width: { xs: "100%", md: "auto" } }}
                >
                  <Button
                    startIcon={<RefreshRoundedIcon />}
                    onClick={loadPromotions}
                    sx={{
                      borderRadius: "5px",
                      textTransform: "none",
                      fontWeight: 700,
                      color: style.color4,
                      backgroundColor: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      minWidth: { xs: "100%", md: 0 },
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.08)",
                      },
                    }}
                  >
                    Refresh
                  </Button>

                  <Button
                    startIcon={<AddRoundedIcon />}
                    onClick={resetForm}
                    sx={{
                      borderRadius: "5px",
                      textTransform: "none",
                      fontWeight: 700,
                      color: style.color4,
                      backgroundColor: style.accentGreen,
                      minWidth: { xs: "100%", md: 0 },
                      "&:hover": {
                        backgroundColor: style.accentGreen,
                        filter: "brightness(1.05)",
                      },
                    }}
                  >
                    New Promotion
                  </Button>
                </Stack>
              </Stack>
            </Box>

            <Box
              sx={{
                px: { xs: 1.5, sm: 2, md: 3 },
                py: { xs: 1.5, md: 2.5 },
                width: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                overflow: "hidden",
              }}
            >
              <Grid container spacing={2} sx={{ width: "100%", minWidth: 0, m: 0 }}>
                <Grid item xs={12} lg={7}>
                  <Stack spacing={2}>
                    <Paper elevation={0} sx={sectionCardSx}>
                      <Stack spacing={1.5}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <SellRoundedIcon sx={{ color: style.color3 }} />
                          <Typography
                            sx={{
                              color: style.color4,
                              fontWeight: 800,
                              fontSize: "1.05rem",
                            }}
                          >
                            {editingPromotionId ? "Edit Promotion" : "Create Promotion"}
                          </Typography>
                        </Stack>

                        <Grid container spacing={2} sx={{ width: "100%", minWidth: 0, m: 0 }}>
                          <Grid item xs={12}>
                            <TextField
                              label="Title"
                              value={formData.title}
                              onChange={(e) => handleChange("title", e.target.value)}
                              fullWidth
                              size="small"
                              sx={sharedTextFieldSx}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              label="Description"
                              value={formData.description}
                              onChange={(e) => handleChange("description", e.target.value)}
                              fullWidth
                              size="small"
                              multiline
                              minRows={2}
                              sx={sharedTextFieldSx}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small" sx={sharedTextFieldSx}>
                              <InputLabel>Promotion Type</InputLabel>
                              <Select
                                value={formData.promoType}
                                label="Promotion Type"
                                onChange={(e) =>
                                  handleChange("promoType", e.target.value)
                                }
                              >
                                <MenuItem value="percentage_off">
                                  Percentage Off
                                </MenuItem>
                                <MenuItem value="fixed_amount_off">
                                  Fixed Amount Off
                                </MenuItem>
                                <MenuItem value="bogo">BOGO</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small" sx={sharedTextFieldSx}>
                              <InputLabel>Scope</InputLabel>
                              <Select
                                value={formData.scopeType}
                                label="Scope"
                                onChange={(e) =>
                                  handleChange("scopeType", e.target.value)
                                }
                              >
                                <MenuItem value="global">Global</MenuItem>
                                <MenuItem value="products">Specific Products</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          {formData.scopeType === "products" && (
                            <Grid item xs={12}>
                              <TextField
                                label="Product SKUs"
                                value={formData.productSKUs}
                                onChange={(e) =>
                                  handleChange("productSKUs", e.target.value)
                                }
                                fullWidth
                                size="small"
                                helperText="Comma-separated list, example: 10001, 10002"
                                sx={sharedTextFieldSx}
                              />
                            </Grid>
                          )}

                          {formData.promoType === "percentage_off" && (
                            <Grid item xs={12}>
                              <TextField
                                label="Discount Percent"
                                type="number"
                                value={formData.discountPercent}
                                onChange={(e) =>
                                  handleChange("discountPercent", e.target.value)
                                }
                                fullWidth
                                size="small"
                                helperText="Example: 25 for 25% off"
                                sx={sharedTextFieldSx}
                              />
                            </Grid>
                          )}

                          {formData.promoType === "fixed_amount_off" && (
                            <Grid item xs={12}>
                              <TextField
                                label="Discount Amount"
                                type="number"
                                value={formData.discountAmount}
                                onChange={(e) =>
                                  handleChange("discountAmount", e.target.value)
                                }
                                fullWidth
                                size="small"
                                helperText="Example: 10 for $10 off"
                                sx={sharedTextFieldSx}
                              />
                            </Grid>
                          )}

                          {formData.promoType === "bogo" && (
                            <>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  label="Buy Quantity"
                                  type="number"
                                  value={formData.buyQuantity}
                                  onChange={(e) =>
                                    handleChange("buyQuantity", e.target.value)
                                  }
                                  fullWidth
                                  size="small"
                                  sx={sharedTextFieldSx}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  label="Get Quantity"
                                  type="number"
                                  value={formData.getQuantity}
                                  onChange={(e) =>
                                    handleChange("getQuantity", e.target.value)
                                  }
                                  fullWidth
                                  size="small"
                                  sx={sharedTextFieldSx}
                                />
                              </Grid>

                              <Grid item xs={12}>
                                <TextField
                                  label="Percent Off Get Items"
                                  type="number"
                                  value={formData.discountPercent}
                                  onChange={(e) =>
                                    handleChange("discountPercent", e.target.value)
                                  }
                                  fullWidth
                                  size="small"
                                  helperText="Use 100 for a normal BOGO free offer. Example: 30 = buy one, get one 30% off."
                                  sx={sharedTextFieldSx}
                                />
                              </Grid>
                            </>
                          )}

                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Priority"
                              type="number"
                              value={formData.priority}
                              onChange={(e) =>
                                handleChange("priority", e.target.value)
                              }
                              fullWidth
                              size="small"
                              sx={sharedTextFieldSx}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Banner Text"
                              value={formData.bannerText}
                              onChange={(e) =>
                                handleChange("bannerText", e.target.value)
                              }
                              fullWidth
                              size="small"
                              sx={sharedTextFieldSx}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Start Date"
                              type="datetime-local"
                              value={formData.startDate}
                              onChange={(e) =>
                                handleChange("startDate", e.target.value)
                              }
                              fullWidth
                              size="small"
                              InputLabelProps={{ shrink: true }}
                              sx={sharedTextFieldSx}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="End Date"
                              type="datetime-local"
                              value={formData.endDate}
                              onChange={(e) =>
                                handleChange("endDate", e.target.value)
                              }
                              fullWidth
                              size="small"
                              InputLabelProps={{ shrink: true }}
                              sx={sharedTextFieldSx}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              label="Internal Notes"
                              value={formData.internalNotes}
                              onChange={(e) =>
                                handleChange("internalNotes", e.target.value)
                              }
                              fullWidth
                              size="small"
                              multiline
                              minRows={3}
                              sx={sharedTextFieldSx}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={formData.isActive}
                                  onChange={(e) =>
                                    handleChange("isActive", e.target.checked)
                                  }
                                  sx={{
                                    color: style.color3,
                                    "&.Mui-checked": {
                                      color: style.accentGreen,
                                    },
                                  }}
                                />
                              }
                              label="Promotion is active"
                              sx={{
                                color: style.color4,
                                alignItems: "flex-start",
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Stack>
                    </Paper>

                    <Paper elevation={0} sx={sectionCardSx}>
                      <Stack spacing={1.25}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <TuneRoundedIcon sx={{ color: style.color3 }} />
                          <Typography
                            sx={{
                              color: style.color4,
                              fontWeight: 800,
                              fontSize: "1.05rem",
                            }}
                          >
                            Save Changes
                          </Typography>
                        </Stack>

                        <Typography
                          sx={{
                            color: style.color3,
                            lineHeight: 1.6,
                            fontSize: "0.92rem",
                          }}
                        >
                          Save your promotion when you’re ready, or cancel editing
                          to start fresh.
                        </Typography>

                        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                        >
                          <Button
                            startIcon={<SaveRoundedIcon />}
                            onClick={handleSave}
                            disabled={saving}
                            sx={{
                              borderRadius: "5px",
                              textTransform: "none",
                              fontWeight: 700,
                              color: style.color4,
                              backgroundColor: style.accentGreen,
                              width: { xs: "100%", sm: "auto" },
                              "&:hover": {
                                backgroundColor: style.accentGreen,
                                filter: "brightness(1.05)",
                              },
                              "&.Mui-disabled": {
                                color: "rgba(255,255,255,0.45)",
                                backgroundColor: "rgba(255,255,255,0.08)",
                              },
                            }}
                          >
                            {saving
                              ? "Saving..."
                              : editingPromotionId
                              ? "Update Promotion"
                              : "Create Promotion"}
                          </Button>

                          {editingPromotionId && (
                            <Button
                              onClick={resetForm}
                              sx={{
                                borderRadius: "5px",
                                textTransform: "none",
                                fontWeight: 700,
                                color: style.color4,
                                backgroundColor: "rgba(255,255,255,0.05)",
                                border: "1px solid rgba(255,255,255,0.08)",
                                width: { xs: "100%", sm: "auto" },
                                "&:hover": {
                                  backgroundColor: "rgba(255,255,255,0.08)",
                                },
                              }}
                            >
                              Cancel Edit
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    </Paper>
                  </Stack>
                </Grid>

                <Grid item xs={12} lg={5}>
                  <Paper elevation={0} sx={sectionCardSx}>
                    <Stack spacing={1.5}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <ViewListRoundedIcon sx={{ color: style.color3 }} />
                        <Typography
                          sx={{
                            color: style.color4,
                            fontWeight: 800,
                            fontSize: "1.05rem",
                          }}
                        >
                          Existing Promotions
                        </Typography>
                      </Stack>

                      {loading ? (
                        <Typography sx={{ color: style.color3 }}>
                          Loading promotions...
                        </Typography>
                      ) : promotions.length === 0 ? (
                        <Typography sx={{ color: style.color3 }}>
                          No promotions found.
                        </Typography>
                      ) : (
                        <Stack spacing={1.25}>
                          {promotions.map((promotion) => (
                            <Paper
                              key={promotion._id}
                              elevation={0}
                              sx={{
                                p: 1.4,
                                borderRadius: "10px",
                                backgroundColor: "rgba(255,255,255,0.03)",
                                border: "1px solid rgba(255,255,255,0.06)",
                              }}
                            >
                              <Stack spacing={1}>
                                <Stack
                                  direction={{ xs: "column", sm: "row" }}
                                  justifyContent="space-between"
                                  spacing={1}
                                  alignItems={{ xs: "flex-start", sm: "center" }}
                                >
                                  <Box>
                                    <Typography
                                      sx={{
                                        color: style.color4,
                                        fontWeight: 800,
                                        lineHeight: 1.3,
                                      }}
                                    >
                                      {promotion.title}
                                    </Typography>

                                    <Typography
                                      sx={{
                                        color: style.color3,
                                        fontSize: "0.84rem",
                                        lineHeight: 1.5,
                                      }}
                                    >
                                      {promotion.promoType} • {promotion.scopeType}
                                    </Typography>
                                  </Box>

                                  <Chip
                                    label={
                                      promotion.currentlyValid
                                        ? "Currently Valid"
                                        : promotion.isActive
                                        ? "Scheduled / Inactive"
                                        : "Disabled"
                                    }
                                    size="small"
                                    sx={{
                                      color: style.color4,
                                      backgroundColor: promotion.currentlyValid
                                        ? "rgba(80,180,120,0.22)"
                                        : "rgba(255,255,255,0.06)",
                                      border: "1px solid rgba(255,255,255,0.08)",
                                      borderRadius: "5px",
                                      fontWeight: 700,
                                    }}
                                  />
                                </Stack>

                                <Typography
                                  sx={{
                                    color: style.color3,
                                    fontSize: "0.88rem",
                                    lineHeight: 1.5,
                                  }}
                                >
                                  {getPromotionSummary(promotion)}
                                </Typography>

                                {promotion.description && (
                                  <Typography
                                    sx={{
                                      color: style.color3,
                                      fontSize: "0.9rem",
                                      lineHeight: 1.55,
                                    }}
                                  >
                                    {promotion.description}
                                  </Typography>
                                )}

                                {promotion.scopeType === "products" &&
                                  Array.isArray(promotion.productSKUs) &&
                                  promotion.productSKUs.length > 0 && (
                                    <Typography
                                      sx={{
                                        color: style.color3,
                                        fontSize: "0.84rem",
                                        lineHeight: 1.5,
                                      }}
                                    >
                                      SKUs: {promotion.productSKUs.join(", ")}
                                    </Typography>
                                  )}

                                <Typography
                                  sx={{
                                    color: style.color3,
                                    fontSize: "0.82rem",
                                    lineHeight: 1.5,
                                  }}
                                >
                                  Priority: {promotion.priority} | Start:{" "}
                                  {formatDateTime(promotion.startDate)} | End:{" "}
                                  {formatDateTime(promotion.endDate)}
                                </Typography>

                                <Divider
                                  sx={{ borderColor: "rgba(255,255,255,0.08)" }}
                                />

                                <Stack direction="row" spacing={1}>
                                  <Button
                                    size="small"
                                    startIcon={<EditRoundedIcon />}
                                    onClick={() => startEdit(promotion)}
                                    sx={{
                                      borderRadius: "5px",
                                      textTransform: "none",
                                      fontWeight: 700,
                                      color: style.color4,
                                      backgroundColor: "rgba(255,255,255,0.05)",
                                      border: "1px solid rgba(255,255,255,0.08)",
                                      "&:hover": {
                                        backgroundColor: "rgba(255,255,255,0.08)",
                                      },
                                    }}
                                  >
                                    Edit
                                  </Button>

                                  <Button
                                    size="small"
                                    startIcon={<DeleteRoundedIcon />}
                                    onClick={() => handleDelete(promotion._id)}
                                    sx={{
                                      borderRadius: "5px",
                                      textTransform: "none",
                                      fontWeight: 700,
                                      color: "#ffb4b4",
                                      backgroundColor: "rgba(255,255,255,0.03)",
                                      border: "1px solid rgba(255,255,255,0.08)",
                                      "&:hover": {
                                        backgroundColor: "rgba(255,255,255,0.06)",
                                      },
                                    }}
                                  >
                                    Delete
                                  </Button>
                                </Stack>
                              </Stack>
                            </Paper>
                          ))}
                        </Stack>
                      )}
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>
      </Box>
    </>
  );
}